import React, { JSX, forwardRef } from 'react';
import { Modal as RNModal } from '@utils/RNModal'
import { Text as DefaultText, View as DefaultView, TextInput as DefaultTextInput, Easing, FlatList as DefaultFlatList, useWindowDimensions, Platform, TouchableWithoutFeedback, Keyboard, Pressable, PressableProps, VirtualizedList, ImageBackground, Image, ImageSourcePropType } from 'react-native';
import { SizableText as DefaultTitle, Separator, H6, Progress, XStack, Dialog, Adapt, Sheet } from 'tamagui';
import { Animated, StyleSheet, Dimensions } from 'react-native';
import {ScrollView as DefaultScrollView} from 'react-native-gesture-handler';
import DefaultModal, { ModalProps as DefaultModalProps } from 'react-native-modal';
import Colors from './Colors';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/types/DefaultRootState';
import { BlurViewProps, BlurView as DefaultBlurView } from '@react-native-community/blur';
import styles from '../../stylesheet';
import { useState, useMemo, useCallback, useEffect } from 'react';
import * as Animatable from 'react-native-animatable';
import { DropDownPickerProps as DefaultDropDownPickerProps } from 'react-native-dropdown-picker';
import DefaultDropDownPicker from 'react-native-dropdown-picker';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import AnimatedLottieView from '@utils/lottieAnimation';
import { AntDesign, MaterialIcons } from '@expo/vector-icons';
import AchievementBackground from '@assets/--native/AchievementBackground';
import Checkbox, { CheckboxProps as DefaultCheckboxProps } from 'expo-checkbox';
import { SizableText, YStack } from 'tamagui';
import { indicatorColor } from 'react-native-calendars/src/style';
import { useDeviceType } from '@hooks/useDeviceType';
import { Card as DefaultCard } from 'tamagui';
import ErrorBoundary from '@utils/ErrorBoundary';
import CloseButton from '@components/UI/CloseButton';
import { LinearGradient } from 'expo-linear-gradient';
import NativeProgressBar from '@components/Global/NativeProgressBar';
import CardFrostStrip from '@components/Global/FrostedCard/CardFrostStrip';
import type { FrostTone, FrostColors } from '@components/Global/FrostedCard/frostTokens';
import {
  getAdaptiveModalTheme,
  MODAL_CONTENT_Z_INDEX,
  MODAL_OVERLAY_Z_INDEX,
  MODAL_PORTAL_Z_INDEX,
  NATIVE_SHEET_Z_INDEX,
} from '@components/modals/shared/adaptiveModalTheme';
import { responsiveFontSize } from '@utils/responsiveFontSize';

// Conditionally import MUI LinearProgress only on web
let MuiLinearProgress: any;
if (Platform.OS === 'web') {
  try {
    MuiLinearProgress = require('@mui/material/LinearProgress').default;
  } catch (e) {
    // MUI not available, will fall back to native component
    console.warn('MUI LinearProgress not available, using native fallback');
  }
}

export function useThemeColor(
  props: { light?: string; dark?: string },
  colorName: keyof typeof Colors.light & keyof typeof Colors.dark
) {
  //TODO UNDO THIS ON DEV PREVIEW
  const isLight = useSelector((state: DefaultRootState) => state.app.theme.lightTheme);
  const theme = isLight ? 'light' : 'dark';
  const colorFromProps = props[theme];

  if (colorFromProps) {
    return colorFromProps;
  } else {
    return Colors[theme][colorName];
  }
}

type ThemeProps = {
  lightColor?: string;
  darkColor?: string;
  extraChildren?: React.ReactNode;
  containerStyle?: Object;
};

type CustomFlatListProps = {
  contentHeight?: number;
}

type CustomTitleProps = {
  selectable?: boolean;
  underline?: boolean;
  bold?: boolean;
};

type StyledModalInternalProps = {
  title?: string,
  caption?: string,
  hideTitleAndCaption?: boolean,
  animated?: boolean,
  hideOverflow?: boolean,
  hideCloseButton?: boolean,
  animation?: string,
  infoChildren?: JSX.Element | JSX.Element[],
  customContent?: JSX.Element | JSX.Element[];
  extraChildren?: JSX.Element | JSX.Element[];
  /** Optional MaterialIcons name shown in the header badge */
  iconName?: keyof typeof MaterialIcons.glyphMap;
  iconColor?: string;
  onModalWillShow?: () => void;
  onModalWillHide?: () => void;
  onModalHide?: () => void;
  isVisible?: boolean;
  setVisible?: (visible: boolean) => void;
  /** Dim the page behind the modal. Defaults to true. */
  hasBackdrop?: boolean;
  backdropColor?: string;
  backdropOpacity?: number;
  /** Max snap point (percent of screen height) for the native sheet. Defaults to 88. */
  maxSnapPoint?: number;
}

type ModalOnly = {
  modalRef?: React.RefObject<StyledModalHandle>;
}

type CustomViewProps = {
  withBackground?: boolean;
}

type CustomTextProps = {
  avoidCalc?: boolean;
}

type AvatarProps = ThemeProps & {
  src?: string | ImageSourcePropType;
  alt?: string;
  size?: number;
  children?: string;
  style?: Object;
  textStyle?: Object;
}

type AvatarGroupProps = ThemeProps & {
  avatars: Array<{
    src?: string | ImageSourcePropType;
    alt?: string;
    name?: string;
  }>;
  max?: number;
  size?: number;
  spacing?: number;
  style?: Object;
  renderSurplus?: (surplus: number) => React.ReactNode;
}

export type TextProps = ThemeProps & CustomTextProps & DefaultText['props'];
export type ViewProps = ThemeProps & DefaultView['props'];
type DefaultTitleProps = React.ComponentProps<typeof DefaultTitle>;
export type TitleProps = ThemeProps & DefaultTitleProps & CustomTitleProps;
export type TextInputProps = ThemeProps & DefaultTextInput['props'];
export type ModalProps = ThemeProps & DefaultModalProps & ModalOnly & { customChildrenHeight?: number, containerStyle?: Object } & { customContent?: JSX.Element | JSX.Element[] } & { onModalWillHide?: () => void } & { modalRef?: React.RefObject<StyledModalHandle> } & { animation?: string } & { infoChildren?: JSX.Element | JSX.Element[] } & { title?: string } & { caption?: string } & { fullBleed?: boolean };
export type ScrollViewProps = ThemeProps & DefaultScrollView['props'];
export type DropDownPickerProps<T> = ThemeProps & DefaultDropDownPickerProps<T>['props'] & { theme?: boolean };
export type FlatListProps = ThemeProps & DefaultFlatList['props'] & CustomFlatListProps;
export type StyledModalProps = Partial<ModalProps & StyledModalInternalProps & { fullScreen?: boolean, onClose?: () => void }>;
export type CheckboxProps = ThemeProps & DefaultCheckboxProps;
export type CardProps = ThemeProps & React.ComponentProps<typeof DefaultCard> & {
  withBackground?: boolean;
  /** Top frost strip (opt-in: pass frost or frost={true}). */
  frost?: boolean;
  frostTone?: FrostTone;
  frostColors?: FrostColors | string[];
  frostCompact?: boolean;
  frostHeight?: number;
};

export type LinearProgressProps = ThemeProps & {
  value?: number;
  variant?: 'determinate' | 'indeterminate';
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' | 'inherit';
  sx?: any;
  style?: any;
};

export const LinearProgress = (props: LinearProgressProps) => {
  const { value, variant = 'indeterminate', color = 'primary', sx, style, ...otherProps } = props;
  const theme = themeSelector();

  if (Platform.OS === 'web' && MuiLinearProgress) {
    // Use MUI LinearProgress on web
    return <MuiLinearProgress value={value} variant={variant} color={color} sx={sx} {...otherProps} />;
  }

  const fillColor = color === 'primary' ? '#1976d2' :
    color === 'secondary' ? '#9c27b0' :
      color === 'error' ? '#d32f2f' :
        color === 'success' ? '#2e7d32' :
          color === 'warning' ? '#ed6c02' :
            '#1976d2';

  return (
    <NativeProgressBar
      value={variant === 'indeterminate' ? 50 : (value ?? 0)}
      max={100}
      height={4}
      backgroundColor={theme ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'}
      fillColor={fillColor}
      style={style}
      {...otherProps}
    />
  );
}

export function Card(props: CardProps & { noShadow?: boolean, cardTitle?: string, disableAnimation?: boolean, cardHeaderComponent?: React.ReactNode, glass?: boolean, elevated?: boolean, glow?: boolean, bordered?: boolean, /** When false, children size to content instead of flex-fill (feed cards in Virtuoso). */ contentFlex?: boolean }) {
  const theme = themeSelector();
  const disableAnimation = props.disableAnimation ?? true;
  const disableShadow = props.noShadow ?? false;
  const contentFlex = props.contentFlex ?? true;

  // Extract scale-related props to prevent override
  const {
    scale: propsScale,
    hoverStyle: propsHoverStyle,
    pressStyle: propsPressStyle,
    glass,
    elevated,
    glow,
    bordered,
    style,
    contentFlex: _contentFlex,
    frost,
    frostTone = 'default',
    frostColors,
    frostCompact,
    frostHeight,
    backgroundColor: backgroundColorProp,
    ...restProps
  } = props;

  const resolvedBg = backgroundColorProp || (theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(40, 40, 40, 0.95)');
  const frostEnabled = frost === true;

  // Ensure scale is always a number
  const scaleValue = typeof propsScale === 'number' ? propsScale : (!disableAnimation ? 0.9 : 1);
  const hoverScale = typeof propsHoverStyle?.scale === 'number' ? propsHoverStyle.scale : (!disableAnimation ? 0.925 : 1);
  const pressScale = typeof propsPressStyle?.scale === 'number' ? propsPressStyle.scale : (!disableAnimation ? 0.875 : 1);

  // Use default Card component
  return (
    <ErrorBoundary>
      <DefaultCard
        unstyled
        // animation={!disableAnimation && 'bouncy'}
        hoverStyle={{ ...propsHoverStyle, scale: hoverScale }}
        pressStyle={{ ...propsPressStyle, scale: pressScale }}
        scale={scaleValue}
        padding={props.padding || 16}
        borderRadius={props.borderRadius || 16}
        backgroundColor={resolvedBg}
        borderWidth={props.borderWidth || (bordered ? 2 : 1)}
        borderColor={props.borderColor || (theme ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)')}
        shadowRadius={disableShadow ? 0 : (elevated ? 20 : 10)}
        shadowOpacity={Platform.select({ web: disableShadow ? 0 : theme ? (elevated ? 0.12 : 0.08) : (elevated ? 0.25 : 0.15), default: disableShadow ? 0 : theme ? 0.6 : 0.6 })}
        shadowColor={glow ? '$primary' : (theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.4)')}
        shadowOffset={{ width: 0, height: elevated ? 12 : 4 }}
        elevation={disableShadow ? 0 : (elevated ? 8 : 2)}
        overflow={frostEnabled ? 'hidden' : props.overflow}
        style={StyleSheet.flatten(style)}
        {...restProps}
      >
        {frostEnabled ? (
          <CardFrostStrip
            tone={frostTone}
            colors={frostColors}
            theme={Boolean(theme)}
            compact={frostCompact}
            height={frostHeight}
            enabled
          />
        ) : null}
        {
          props.cardTitle &&
          <YStack style={{ marginBottom: 20, top: -10, margin: 10, zIndex: 1 }}>
            <XStack marginBottom={3}>
              <H6 style={{ fontWeight: 'bold', flex: 1, textAlign: 'left' }} color={theme ? 'black' : 'white'}>
                {props.cardTitle}
              </H6>
              {
                props.cardHeaderComponent
              }
            </XStack>
            <Separator />
          </YStack>
        }
        <View style={contentFlex ? { flex: 1, zIndex: 1 } : { width: '100%', alignSelf: 'stretch', zIndex: 1 }}>
          {props.children}
        </View>
      </DefaultCard>
    </ErrorBoundary>
  )
}

// Forward static subcomponents - use default components
Card.Header = DefaultCard.Header
Card.Footer = DefaultCard.Footer
Card.Background = DefaultCard.Background
Card.Title = DefaultCard.Title
Card.Description = DefaultCard.Description
Card.Content = DefaultCard.Content

export function SmallCard(props: CardProps & { cardTitle?: string, disableAnimation?: boolean, cardHeaderComponent?: React.ReactNode }) {
  const theme = themeSelector();
  const disableAnimation = props.disableAnimation ?? true;

  // Extract scale-related props to prevent override
  const {
    scale: propsScale,
    hoverStyle: propsHoverStyle,
    pressStyle: propsPressStyle,
    style,
    frost,
    frostTone = 'default',
    frostColors,
    frostCompact,
    frostHeight,
    backgroundColor: backgroundColorProp,
    ...restProps
  } = props;

  const resolvedBg = backgroundColorProp || (theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(40, 40, 40, 0.95)');
  const frostEnabled = frost === true;

  // Ensure scale is always a number
  const scaleValue = typeof propsScale === 'number' ? propsScale : (!disableAnimation ? 0.9 : 1);
  const hoverScale = typeof propsHoverStyle?.scale === 'number' ? propsHoverStyle.scale : (!disableAnimation ? 0.925 : 1);
  const pressScale = typeof propsPressStyle?.scale === 'number' ? propsPressStyle.scale : (!disableAnimation ? 0.875 : 1);

  // Use default Card component
  return (
    <ErrorBoundary>
      <DefaultCard
        unstyled
        animation={!disableAnimation ? 'bouncy' : undefined}
        hoverStyle={{ ...propsHoverStyle, scale: hoverScale }}
        pressStyle={{ ...propsPressStyle, scale: pressScale }}
        scale={scaleValue}
        padding={props.padding || 12}
        borderRadius={props.borderRadius || 16}
        backgroundColor={resolvedBg}
        borderWidth={props.borderWidth || 1}
        borderColor={props.borderColor || (theme ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)')}
        shadowRadius={8}
        shadowOpacity={theme ? 0.12 : 0.2}
        shadowColor={theme ? 'rgba(0, 0, 0, 0.08)' : 'rgba(0, 0, 0, 0.3)'}
        shadowOffset={{ width: 0, height: 2 }}
        elevation={6}
        overflow={frostEnabled ? 'hidden' : props.overflow}
        style={StyleSheet.flatten(style)}
        {...restProps}
      >
        {frostEnabled ? (
          <CardFrostStrip
            tone={frostTone}
            colors={frostColors}
            theme={Boolean(theme)}
            compact={frostCompact ?? true}
            height={frostHeight}
            enabled
          />
        ) : null}
        {
          props.cardTitle &&
          <YStack style={{ marginBottom: 20, top: -10, margin: 10, zIndex: 1 }}>
            <XStack>
              <Text style={styles.title}>{props.cardTitle}</Text>
              {
                props.cardHeaderComponent
              }
            </XStack>
          </YStack>
        }
        {props.children}
      </DefaultCard>
    </ErrorBoundary>
  )
}

export function Text(props: TextProps) {
  const { style, lightColor, avoidCalc, numberOfLines, children, darkColor, ...otherProps } = props;
  const color = useThemeColor({ light: lightColor, dark: darkColor }, 'text');
  let lineNum = 1;
  const appLanguage = useSelector((state: DefaultRootState) => state.app.language);

  const calcLines = () => {
    const textLength = children?.toString().length;
    if (textLength) {
      if (textLength > 40) {
        return 3;
      } else if (textLength > 20) {
        return 2;
      }
    }
    return 1;
  }

  return Platform.OS != 'web' ?
    <DefaultText selectable={props.selectable ?? false} style={[{ flexWrap: "wrap", color, lineHeight: 30 }, style]} {...otherProps}>{children}</DefaultText>
    : <DefaultText numberOfLines={!avoidCalc && (numberOfLines ?? calcLines())} adjustsFontSizeToFit={props.adjustsFontSizeToFit ?? true} selectable={props.selectable ?? false} style={StyleSheet.flatten([{ flexWrap: "wrap", color, lineHeight: 30 }, style])} {...otherProps}>{children}</DefaultText>;
}

export function View(props: ViewProps & CustomViewProps) {
  const { style, lightColor, darkColor, withBackground, ...otherProps } = props;
  const backgroundColor = useThemeColor({ light: lightColor, dark: darkColor }, 'background');

  const resolvedStyle = StyleSheet.flatten([
    withBackground && { backgroundColor },
    style,
  ]);

  // Web: Animatable backgroundColor transition retriggers RN Web styleq on every render (app hang on load).
  if (Platform.OS === 'web') {
    return <DefaultView style={resolvedStyle} {...otherProps} />;
  }
  return <Animatable.View transition={'backgroundColor'} style={resolvedStyle} {...otherProps} />;
}


export function FlatList(props: FlatListProps) {
  const { style, data, children, contentContainerStyle, showsVerticalScrollIndicator } = props;
  const windowHeight = Dimensions.get('window').height;
  const [viewHeight, setViewHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const scrollY = React.useRef(new Animated.Value(0)).current;
  const theme = themeSelector();

  const indicatorHeight = viewHeight / (contentHeight / viewHeight); // Adjust scroll indicator height
  const translateY = scrollY.interpolate({
    inputRange: [0, contentHeight - viewHeight],
    outputRange: [0, viewHeight - indicatorHeight],
    extrapolate: 'clamp',
  });

  const handleScroll = typeof props.onScroll === "function" ? props.onScroll : () => { };

  return (
    <Animated.View style={{ flex: 1, flexDirection: 'row', gap: 20 }}>
      <FlatList
        {...props}
        style={[scrollStyles.scrollView, { flex: 1, backgroundColor: 'transparent', width: '100%', height: '100%' }, style]}
        onContentSizeChange={(_, eventHieght) => { console.log(eventHieght.valueOf()); setViewHeight(eventHieght.valueOf()) }}
        contentContainerStyle={[scrollStyles.contentContainer, contentContainerStyle]}
        // onScroll={Animated.event(
        //   [{ nativeEvent: { contentOffset: { y: scrollY } } }],
        //   { useNativeDriver: true }
        // )}
        scrollEventThrottle={16}
        showsVerticalScrollIndicator={false}
        showsHorizontalScrollIndicator={false}
      >
      </FlatList>
      {
        data?.length > 0 && viewHeight > (windowHeight) && showsVerticalScrollIndicator &&
        <View
          style={{
            backgroundColor: theme ? '#ececec' : 'grey',
            marginHorizontal: 10,
            minWidth: 10,
            paddingBottom: '20%',
            borderRadius: 999,
            overflow: 'hidden',
            marginVertical: "4%"
          }}
        >
          <Animated.View
            style={{
              width: '100%',
              position: 'absolute',
              height: indicatorHeight,
              transform: [{ translateY }],
              backgroundColor: indicatorColor || 'rgb(90,192,234)', // Adjust as needed
            }}
          />
        </View>
      }
    </Animated.View>
  )
}

export const ScrollView = React.forwardRef((
  props: ScrollViewProps & { scrollEnabled?: boolean, inverse?: boolean, indicatorColor?: string },
  ref: React.Ref<DefaultScrollView>
) => {
  const { inverse, indicatorColor, horizontal, scrollEnabled } = props;
  const [viewHeight, setViewHeight] = useState(100);
  const [viewWidth, setViewWidth] = useState(100);
  const [contentHeight, setContentHeight] = useState(200);
  const [contentWidth, setContentWidth] = useState(200);
  const scrollY = React.useRef(new Animated.Value(0)).current;
  const scrollX = React.useRef(new Animated.Value(0)).current;
  const theme = themeSelector();

  const isScrollable = horizontal
    ? contentWidth > viewWidth
    : contentHeight > viewHeight;

  const indicatorHeight = !horizontal && isScrollable
    ? Math.max(viewHeight * (viewHeight / contentHeight), 20)
    : 0;

  const indicatorWidth = horizontal && isScrollable
    ? Math.max(viewWidth * (viewWidth / contentWidth), 20)
    : 0;

  const maxVerticalScroll = Math.max(1, contentHeight - viewHeight);
  const maxHorizontalScroll = Math.max(1, contentWidth - viewWidth);

  const translateY = scrollY.interpolate({
    inputRange: [0, maxVerticalScroll],
    outputRange: [0, Math.max(0, viewHeight - indicatorHeight)],
    extrapolate: 'clamp',
  });

  const translateX = scrollX.interpolate({
    inputRange: [0, maxHorizontalScroll],
    outputRange: [0, Math.max(0, viewWidth - indicatorWidth)],
    extrapolate: 'clamp',
  });

  const handleScroll = (e) => {
    const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;

    setContentHeight(contentSize.height);
    setViewHeight(layoutMeasurement.height);
    setContentWidth(contentSize.width);
    setViewWidth(layoutMeasurement.width);

    scrollY.setValue(contentOffset.y);
    scrollX.setValue(contentOffset.x);
  };

  return (
    <View style={[scrollStyles.container, {
      flexDirection: horizontal ? 'column' : (inverse ? 'row-reverse' : 'row')
    }, props.containerStyle]}>
      <DefaultScrollView
        {...props}
        ref={ref}
        style={[scrollStyles.scrollView, props.style]}
        horizontal={horizontal}
        showsVerticalScrollIndicator={false}
        showsHorizontalScrollIndicator={false}
        onContentSizeChange={(w, h) => {
          setContentHeight(h);
          setContentWidth(w);
        }}
        onLayout={(e) => {
          setViewHeight(e.nativeEvent.layout.height);
          setViewWidth(e.nativeEvent.layout.width);
        }}
        onScroll={handleScroll}
        scrollEventThrottle={16}
      >
        {props.children}
      </DefaultScrollView>
      {/* Vertical Scrollbar */}
      {!horizontal && isScrollable && scrollEnabled && (
        <View
          style={{
            backgroundColor: theme ? '#ececec' : 'grey',
            marginLeft: 20,
            minWidth: 5,
            top: 5,
            bottom: 5,
            borderRadius: 999,
            overflow: 'hidden',
            marginTop: props.style?.['marginTop'] || "10%",
            marginBottom: props.style?.['marginBottom'] || "10%",
            marginVertical: props.style?.['marginVertical'] || "10%",
          }}
        >
          <Animated.View
            style={{
              width: '100%',
              height: indicatorHeight,
              borderRadius: 999,
              transform: [{ translateY }],
              backgroundColor: indicatorColor || 'rgb(90,192,234)',
            }}
          />
        </View>
      )}

      {/* Horizontal Scrollbar */}
      {horizontal && isScrollable && scrollEnabled && (
        <View
          style={{
            backgroundColor: theme ? 'white' : 'grey',
            height: 5,
            position: 'absolute',
            bottom: 10,
            left: props.style['marginLeft'] || 5,
            right: 5,
            borderRadius: 999,
            overflow: 'hidden',
            ...props.style['marginLeft'],
            ...props.style['marginRight'],
            ...props.style['marginHorizontal'],
          }}
        >
          <Animated.View
            style={{
              height: '100%',
              width: indicatorWidth,
              borderRadius: 999,
              transform: [{ translateX }],
              backgroundColor: indicatorColor || 'rgb(90,192,234)',
            }}
          />
        </View>
      )}
    </View>
  );
});



const scrollStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  contentContainer: {
    paddingVertical: 20,
    flex: 1,
  },
  scrollIndicator: {
    position: 'absolute',
    right: 0,
    top: 0,
    width: 3, // Adjusted width of the scroll indicator
    height: '100%', // Adjusted height of the scroll indicator
    backgroundColor: 'rgb(90,192,234)', // Change to your desired color
    borderRadius: 999,
  },
});



export function Title(props: TitleProps) {
  const { style, lightColor, darkColor, bold, underline, ...otherProps } = props;
  const color = useThemeColor({ light: lightColor, dark: darkColor }, 'text');
  const theme = themeSelector();

  // Flatten to a single object so web DOM never receives a style array (CSSStyleDeclaration rejects indexed setter)
  const resolvedStyle = StyleSheet.flatten([
    styles.title,
    { color: theme ? "black" : "white", borderBottomWidth: underline ? 1 : 0, fontWeight: bold ? 'bold' : undefined },
    style,
  ]);

  return <DefaultTitle selectable={props.selectable || false} style={resolvedStyle} {...otherProps} />;
}

const DismissKeyboard: React.FC<PressableProps> = ({ children, hitSlop }) => {
  const handlePress = () => Keyboard.dismiss();

  return (
    <Pressable
      onPress={handlePress}
      style={{ flex: 1 }}
      hitSlop={{ top: hitSlop, bottom: hitSlop, left: hitSlop, right: hitSlop }}
    >
      {children}
    </Pressable>
  );
};

export const TextInput: React.FC<TextInputProps> = React.memo((props: TextInputProps) => {
  const { style, lightColor = 'black', darkColor = 'white', multiline, hitSlop, ...otherProps } = props;
  const color = useThemeColor({ light: lightColor, dark: darkColor }, 'text');
  const [isFocused, setFocused] = useState(false);

  const onFocus = () => setFocused(true);
  const onBlur = () => setFocused(false);

  const textInputProps = {
    style: StyleSheet.flatten([styles.inputOutline, { marginHorizontal: 0, color }, style]),
    cursorColor: color,
    onFocus,
    onBlur,
    ...otherProps,
  };

  return multiline ? useMemo(() => (
    <DismissKeyboard hitSlop={isFocused ? 1000 : 0}>
      <DefaultTextInput {...textInputProps} />
    </DismissKeyboard>
  ), []) : useMemo(() => (
    <DefaultTextInput {...textInputProps} />
  ), []);
});

export function CheckBox(props: CheckboxProps) {
  return (
    <Checkbox
      style={{ backgroundColor: 'transparent' }}
      color={useThemeColor({ light: 'black', dark: 'white' }, 'text')}
      {...props}
    />
  )
}

export function BlurView(props: BlurViewProps) {
  const theme = themeSelector();
  const { style, ...otherProps } = props;



  const blurType = props.blurType ?? (theme ? 'light' : 'dark');
  return Platform.OS === 'android' ? <View style={[{ backgroundColor: theme ? "#ececec50" : "#00000050" }, style]} {...otherProps}></View> : (<DefaultBlurView blurRadius={20} blurType={blurType} style={[{ backgroundColor: Platform.select({ android: "transparent", default: 'transparent' }) }, style]} {...otherProps} />)
}

export function DropDownPicker<T>(props: DropDownPickerProps<T> & DropDownPickerMultipleProps<T>) {
  const globalTheme = themeSelector();
  const theme = props.theme !== undefined ? props.theme : globalTheme;
  const { style, ...otherProps } = props;

  // Don't override backgroundColor if already set in passed styles
  const dropDownContainerStyle = otherProps.dropDownContainerStyle
    ? Array.isArray(otherProps.dropDownContainerStyle)
      ? [...otherProps.dropDownContainerStyle]
      : [otherProps.dropDownContainerStyle]
    : [];
  const hasDropDownBg = dropDownContainerStyle.some(
    (s: any) => s && typeof s === 'object' && 'backgroundColor' in s
  );

  const selectedItemContainerStyle = otherProps.selectedItemContainerStyle
    ? Array.isArray(otherProps.selectedItemContainerStyle)
      ? [...otherProps.selectedItemContainerStyle]
      : [otherProps.selectedItemContainerStyle]
    : [];
  const hasSelectedItemBg = selectedItemContainerStyle.some(
    (s: any) => s && typeof s === 'object' && 'backgroundColor' in s
  );

  return <DefaultDropDownPicker
    style={[style, { backgroundColor: theme ? 'white' : '#333333' }]}
    dropDownContainerStyle={hasDropDownBg
      ? dropDownContainerStyle
      : [...dropDownContainerStyle, { backgroundColor: theme ? 'white' : '#333333' }]}
    textStyle={[otherProps.textStyle, { color: theme ? 'black' : 'white' }]}
    selectedItemContainerStyle={hasSelectedItemBg
      ? selectedItemContainerStyle
      : [...selectedItemContainerStyle, { backgroundColor: theme ? 'white' : '#333333' }]}
    theme={theme ? "LIGHT" : "DARK"}
    {...otherProps}
  />;
}

export function Modal(props: ModalProps & { closeButtonStyle?: ViewProps, customChildrenHeight?: number, hideCloseButton?: boolean, setVisible: (visible: boolean) => void, backgroundColor?: string, closeFunction?: () => void }) {
  const { backgroundColor, extraChildren, customChildrenHeight, containerStyle, style, animationIn, animationOut, hasBackdrop, coverScreen, backdropColor, backdropOpacity, deviceHeight, deviceWidth, children, fullBleed, ...otherProps } = props;
  const theme = themeSelector();
  const { height, width } = useWindowDimensions();
  const { isTablet } = useDeviceType();
  const isLandscape = width > height;
  const supportedOrientations = isLandscape ? ['landscape-left', 'landscape-right'] : ['portrait'];
  React.useEffect(() => {
    //Add listenered for escape key to close modal
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (otherProps.isVisible) {
          otherProps.setVisible?.(false);
        } else {
          try {
            props.modalRef.current?.closeModal();
          } catch (error) {

          }
        }
      }
    }
    if (Platform.OS === 'web') {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      if (Platform.OS === 'web') {
        window.removeEventListener('keydown', handleKeyDown);
      }
    }
  })

  return <DefaultModal
    animationIn={'fadeIn'}
    animationOut={'zoomOutUp'}
    deviceWidth={deviceWidth || width}
    deviceHeight={deviceHeight * 1.2 || height * 1.2}
    presentationStyle="overFullScreen"
    supportedOrientations={supportedOrientations}
    coverScreen={coverScreen !== undefined ? coverScreen : true}
    hasBackdrop={hasBackdrop !== undefined ? hasBackdrop : true}
    backdropColor={backdropColor || 'rgba(0,0,0,0.5)'}
    backdropOpacity={backdropOpacity !== undefined ? backdropOpacity : 0.5}
    style={[
      styles.centerEverything,
      fullBleed
        ? {
            paddingTop: 0,
            paddingBottom: 0,
            zIndex: 300,
            flex: 1,
            gap: 0,
            justifyContent: 'flex-start' as const,
            alignItems: 'stretch' as const,
            alignSelf: 'stretch' as const,
            width: '100%' as const,
            margin: 0,
          }
        : { paddingTop: Platform.select({ native: '10%', web: '5%' }), zIndex: 300, flex: 1, gap: 15 },
      style,
    ]}
    {...otherProps}>
    {
      !props.hideCloseButton &&
      <CloseButton  handleClose={() => { try { otherProps.setVisible(false); props.setVisible(false); } catch { } }}
      style={props.closeButtonStyle}
      />
    }
    <View style={{
      flex: fullBleed ? 1 : backgroundColor === 'transparent' ? 0 : Platform.select({ default: 50, web: undefined }),
      backgroundColor: backgroundColor || (theme ? 'white' : '#333333'),
      borderRadius: backgroundColor === 'transparent' ? 0 : 20,
      minHeight: fullBleed ? ('100%' as const) : backgroundColor === 'transparent' ? undefined : Platform.select({ web: '60%', default: '80%' }),
      ...(fullBleed ? { width: '100%' as const, alignSelf: 'stretch' as const } : {}),
    }} >
      {children}
    </View>
    {
      extraChildren &&
      <View style={{ minWidth: Platform.select({ web: "100%", default: "100%" }), top: isTablet ? "-5%" : undefined, backgroundColor: 'transparent', flex: .1, minHeight: customChildrenHeight || 70 }}>
        {extraChildren}
      </View>
    }
  </DefaultModal>;
}


export type LoadingModalHandle = {
  openModal: () => void;
  closeModal: () => void;
}

export const LoadingModal = React.forwardRef((props: ModalProps, ref) => {
  const { extraChildren, containerStyle, style, ...otherProps } = props;
  const { height, width } = useWindowDimensions();
  const isLandscape = width > height;
  const supportedOrientations = isLandscape ? ['landscape-left', 'landscape-right'] : ['portrait'];
  const [isVisible, setVisible] = useState(false);
  const theme = themeSelector();
  React.useImperativeHandle(ref, () => ({
    openModal: () => {
      setVisible(true);
    },
    closeModal: () => {
      setVisible(false);
    }
  }));

  return <DefaultModal
    animationIn={'fadeIn'}
    animationOut={'zoomOutUp'}
    deviceWidth={width}
    isVisible={isVisible}
    deviceHeight={height}
    presentationStyle="overFullScreen"
    supportedOrientations={supportedOrientations}
    coverScreen={true}
    hasBackdrop={true}
    style={StyleSheet.flatten([styles.centerEverything, { flex: 1, gap: 15, alignItems: "center" }])}
    {...otherProps}>
    <View style={[{ borderRadius: 20, maxWidth: "80%", maxHeight: "60%", backgroundColor: theme ? "white" : "black", margin: 20, marginVertical: 70, alignSelf: "center", justifyContent: "center", alignContent: "center", alignItems: "center", padding: 0, flex: 1, minWidth: "100%", overflow: "visible" }, containerStyle]}>
      <View style={styles.centerEverything}>
        <AnimatedLottieView source={require("@assets/animations/robo-anim.json")} style={{ flex: 3, minHeight: 300, width: 300 }} loop autoPlay />
        <Progress style={{ flex: 1, borderRadius: 10, maxHeight: 15, }} />
      </View>
    </View>
    {
      extraChildren &&
      <View style={{ minWidth: Platform.select({ web: undefined, default: "100%" }), maxWidth: "100%", backgroundColor: 'transparent', flex: 1, minHeight: 70 }}>
        {extraChildren}
      </View>
    }
  </DefaultModal>;
})

export type StyledModalHandle = {
  openModal: (item?: any) => void;
  closeModal: () => void;
  animateView: () => void;
  openAnimated: () => Promise<void>;
}

export const StyledModal = React.forwardRef<StyledModalHandle, StyledModalProps>((props: StyledModalProps, ref) => {
  const {
    title,
    children,
    animated,
    caption,
    hideTitleAndCaption,
    hideCloseButton,
    hideOverflow,
    infoChildren,
    customContent,
    extraChildren,
    fullScreen = false,
    iconName,
    iconColor,
    onModalWillShow,
    onModalWillHide,
    onModalHide,
    onClose,
    isVisible: isVisibleProp,
    setVisible: setVisibleProp,
    containerStyle,
    customChildrenHeight,
    hasBackdrop,
    backdropColor,
    backdropOpacity = 0.5,
    maxSnapPoint = 88,
    ...otherProps
  } = props;
  const showBackdrop = hasBackdrop !== false;

  const [visible, setVisible] = useState(false);
  const [animate, setAnimate] = useState(false);
  const theme = themeSelector();
  const modalTheme = getAdaptiveModalTheme(theme);
  const { isTablet } = useDeviceType();
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();
  const hasPinnedFooter = !!extraChildren;

  const showStandardHeader = !hideTitleAndCaption && !customContent && !!(title || caption);
  const showHero = !!customContent;
  const contentWidth = fullScreen
    ? screenWidth
    : Math.min(isTablet ? 720 : 560, screenWidth * 0.92);
  const contentMaxHeight = fullScreen ? screenHeight : Math.min(screenHeight * (maxSnapPoint / 100), 760);

  useEffect(() => {
    if (typeof isVisibleProp === 'boolean') {
      setVisible(isVisibleProp);
    }
  }, [isVisibleProp]);

  const handleClose = useCallback(() => {
    onModalWillHide?.();
    setVisible(false);
    setVisibleProp?.(false);
    onClose?.();
    setTimeout(() => {
      onModalHide?.();
    }, 220);
  }, [onModalWillHide, onModalHide, onClose, setVisibleProp]);

  const handleOpen = useCallback((item?: unknown) => {
    onModalWillShow?.();
    setVisible(true);
    setVisibleProp?.(true);
    if (item) {
      // preserved for callers that pass context into openModal(item)
    }
  }, [onModalWillShow, setVisibleProp]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && visible) {
        handleClose();
      }
    };
    if (Platform.OS === 'web') {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      if (Platform.OS === 'web') {
        window.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [visible, handleClose]);

  React.useImperativeHandle(ref, () => ({
    openModal: (item?: unknown) => handleOpen(item),
    closeModal: () => handleClose(),
    animateView: () => {
      setAnimate((prev) => !prev);
    },
    openAnimated: async () => {
      handleOpen();
      await new Promise<void>((resolve) => {
        setTimeout(() => {
          setAnimate(true);
          resolve();
        }, 200);
      });
    },
  }));

  const handleOpenChange = useCallback((open: boolean) => {
    if (open) {
      handleOpen();
    } else {
      handleClose();
    }
  }, [handleOpen, handleClose]);

  const overlayColor = backdropColor || '#000';
  const isWeb = Platform.OS === 'web';
  const portalZIndex = isWeb ? MODAL_PORTAL_Z_INDEX : NATIVE_SHEET_Z_INDEX;
  const contentZIndex = isWeb ? MODAL_CONTENT_Z_INDEX : NATIVE_SHEET_Z_INDEX + 2;
  const overlayStyle = useMemo(() => ({
    zIndex: isWeb ? MODAL_OVERLAY_Z_INDEX : NATIVE_SHEET_Z_INDEX + 1,
    backgroundColor: overlayColor,
    ...(isWeb ? { backdropFilter: 'blur(8px)' } : {}),
  }), [overlayColor, isWeb]);

  const contentSurfaceStyle = useMemo(() => ({
    zIndex: contentZIndex,
    padding: 0,
    width: fullScreen ? screenWidth : contentWidth,
    maxWidth: fullScreen ? screenWidth : contentWidth,
    maxHeight: contentMaxHeight,
    flexDirection: 'column' as const,
    alignSelf: 'center' as const,
    borderRadius: fullScreen ? 0 : 24,
    overflow: hideOverflow ? 'hidden' as const : 'hidden' as const,
    backgroundColor: modalTheme.modalSurface,
    borderWidth: fullScreen ? 0 : 1,
    borderColor: modalTheme.panelBorder,
    elevation: isWeb ? 20 : 25,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.15,
    shadowRadius: 32,
    ...(!isWeb ? {
      display: 'flex' as const,
    } : {}),
    ...(isWeb && !fullScreen ? {
       boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
     } : {}),
    ...(containerStyle as object),
  }), [contentMaxHeight, contentWidth, containerStyle, contentZIndex, fullScreen, hideOverflow, isWeb, modalTheme.modalSurface, modalTheme.panelBorder, screenWidth]);

  const modalBodyStyle = useMemo(() => ({
    flexDirection: 'column' as const,
    ...(hasPinnedFooter || !isWeb
      ? { flex: 1, minHeight: 0, maxHeight: contentMaxHeight }
      : {}),
  }), [contentMaxHeight, hasPinnedFooter, isWeb]);

  const modalScrollRegionStyle = useMemo(() => ({
    ...(hasPinnedFooter || !isWeb ? { flex: 1, minHeight: 0, flexShrink: 1 } : {}),
  }), [hasPinnedFooter, isWeb]);

  const modalFooterStyle = useMemo(() => ({
    flexShrink: 0,
    backgroundColor: modalTheme.panelSurface,
    borderTopWidth: 1,
    borderTopColor: modalTheme.panelBorder,
    paddingHorizontal: 24,
    paddingTop: 16,
    paddingBottom: isWeb ? 16 : 16,
  }), [isWeb, modalTheme.panelBorder, modalTheme.panelSurface]);

  // Keep hooks mounted for imperative openModal(), but do NOT keep Dialog/Adapt/Sheet
  // in the tree while closed. On Android Tamagui falls back to GorhomPortalItemFallback,
  // whose useEffect([children]) re-registers portals on every parent re-render and
  // trips "Maximum update depth exceeded" (e.g. DemoMigrationWizard in StaticMenuHeader).
  if (!visible) {
    return null;
  }

  return (
    <Dialog modal open onOpenChange={handleOpenChange}>
      <Adapt platform="touch">
        <Sheet
          modal
          dismissOnSnapToBottom
          animation="medium"
          snapPoints={fullScreen ? [100] : [maxSnapPoint]}
          zIndex={NATIVE_SHEET_Z_INDEX}
        >
          <Sheet.Frame
            padding="$0"
            borderTopLeftRadius={fullScreen ? 0 : 24}
            borderTopRightRadius={fullScreen ? 0 : 24}
            backgroundColor={modalTheme.modalSurface}
            borderWidth={fullScreen ? 0 : 1}
            borderColor={modalTheme.panelBorder}
            flex={1}
          >
            <Adapt.Contents />
          </Sheet.Frame>
          {showBackdrop ? (
            <Sheet.Overlay
              animation="lazy"
              opacity={backdropOpacity}
              backgroundColor={overlayColor}
              enterStyle={{ opacity: 0 }}
              exitStyle={{ opacity: 0 }}
            />
          ) : null}
        </Sheet>
      </Adapt>

      <Dialog.Portal zIndex={portalZIndex}>
        {showBackdrop ? (
          <Dialog.Overlay
            key="styled-modal-overlay"
            animation="quick"
            opacity={backdropOpacity}
            backgroundColor={overlayColor}
            style={overlayStyle}
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
          />
        ) : null}
        <Dialog.Content
          bordered
          elevate
          x={0}
          y={0}
          scale={1}
          opacity={1}
          animation={[
            'quick',
            {
              opacity: {
                overshootClamping: true,
              },
            },
          ]}
          enterStyle={{ x: 0, y: -16, opacity: 0, scale: 0.96 }}
          exitStyle={{ x: 0, y: 8, opacity: 0, scale: 0.98 }}
          style={contentSurfaceStyle}
        >
          {showStandardHeader && (
            <View
              style={{
                flexShrink: 0,
                backgroundColor: modalTheme.panelSurface,
                paddingHorizontal: 24,
                paddingVertical: 20,
                borderBottomWidth: 1,
                borderBottomColor: modalTheme.panelBorder,
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: 12,
              }}
            >
              {iconName ? (
                <View
                  style={{
                    backgroundColor: modalTheme.iconBadgeSurface,
                    borderRadius: 12,
                    padding: 10,
                  }}
                >
                  <MaterialIcons
                    name={iconName}
                    size={20}
                    color={iconColor || modalTheme.iconBadgeColor}
                  />
                </View>
              ) : null}
              <YStack flex={1} gap={4} paddingRight={hideCloseButton ? 0 : 36}>
                {!!title && (
                  <Dialog.Title
                    style={{
                      fontSize: responsiveFontSize(20),
                      fontWeight: '700',
                      color: modalTheme.titleText,
                      margin: 0,
                      padding: 0,
                    }}
                  >
                    {title}
                  </Dialog.Title>
                )}
                {!!caption && (
                  <Dialog.Description
                    style={{
                      fontSize: responsiveFontSize(13),
                      fontWeight: '500',
                      color: modalTheme.subtleText,
                      lineHeight: responsiveFontSize(18),
                      margin: 0,
                    }}
                  >
                    {caption}
                  </Dialog.Description>
                )}
              </YStack>
              {!hideCloseButton && (
                <Dialog.Close asChild displayWhenAdapted>
                  <Pressable
                    onPress={handleClose}
                    style={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      padding: 8,
                      borderRadius: 20,
                      backgroundColor: modalTheme.mutedSurface,
                      borderWidth: 1,
                      borderColor: modalTheme.mutedBorder,
                    }}
                    accessibilityRole="button"
                    accessibilityLabel="Close"
                  >
                    <MaterialIcons name="close" size={20} color={modalTheme.mutedText} />
                  </Pressable>
                </Dialog.Close>
              )}
            </View>
          )}
          <ScrollView style={modalBodyStyle}>

          {showHero && (
            <View
              style={{
                flexShrink: 0,
                overflow: 'hidden',
                borderBottomWidth: extraChildren || children ? 1 : 0,
                borderBottomColor: modalTheme.panelBorder,
              }}
            >
              {animated ? (
                <LinearGradient
                  colors={['#0693e3', '#9b51e0']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={{
                    paddingHorizontal: 24,
                    paddingVertical: fullScreen ? 32 : 24,
                    minHeight: animate ? 180 : 140,
                  }}
                >
                  {customContent}
                </LinearGradient>
              ) : (
                <View
                  style={{
                    backgroundColor: modalTheme.panelSurface,
                    paddingHorizontal: 24,
                    paddingVertical: 20,
                  }}
                >
                  {customContent}
                </View>
              )}
              {!hideCloseButton && (
                <Pressable
                  onPress={handleClose}
                  style={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    padding: 8,
                    borderRadius: 20,
                    backgroundColor: animated ? 'rgba(255,255,255,0.2)' : modalTheme.mutedSurface,
                    borderWidth: 1,
                    borderColor: animated ? 'rgba(255,255,255,0.35)' : modalTheme.mutedBorder,
                  }}
                  accessibilityRole="button"
                  accessibilityLabel="Close"
                >
                  <MaterialIcons
                    name="close"
                    size={20}
                    color={animated ? '#ffffff' : modalTheme.mutedText}
                  />
                </Pressable>
              )}
            </View>
          )}

          {(children || infoChildren) ? (
            <View style={modalScrollRegionStyle}>
            <DefaultScrollView
              style={(hasPinnedFooter || !isWeb) ? { flex: 1 } : undefined}
              contentContainerStyle={{
                paddingHorizontal: 24,
                paddingVertical: 20,
                gap: 16,
                ...(customChildrenHeight ? { minHeight: customChildrenHeight } : {}),
                ...(isWeb && !hasPinnedFooter ? { flexGrow: 1 } : {}),
              }}
              keyboardShouldPersistTaps="handled"
              nestedScrollEnabled
              showsVerticalScrollIndicator
            >
              {infoChildren}
              {children}
            </DefaultScrollView>
            </View>
          ) : null}

          {extraChildren ? (
            <View style={modalFooterStyle}>
              {extraChildren}
            </View>
          ) : null}
          </ScrollView>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
});


export const Badge = ({
  badgeContent,
  color = 'red',
  children,
  max = 99,
  style,
  badgeStyle,
  ...props
}) => {
  let displayContent = badgeContent;
  if (typeof badgeContent === 'number' && badgeContent > max) {
    displayContent = `${max}+`;
  }

  return (
    <View style={[badgeStyle.container, style]} {...props}>
      {children}
      {badgeContent !== undefined && (
        <View style={[badgeStyle.badge, { backgroundColor: color }, badgeStyle]}>
          <Text style={badgeStyle.text}>{displayContent}</Text>
        </View>
      )}
    </View>
  );
};

const badgeStyle = StyleSheet.create({
  container: {
    position: 'relative',
    alignSelf: 'flex-start',
  },
  badge: {
    position: 'absolute',
    top: -6,
    right: -6,
    minWidth: 16,
    height: 16,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  text: {
    color: 'white',
    fontSize: 10,
    fontWeight: 'bold',
  },
});

export const Avatar = ({
  src,
  alt = '',
  size = 40,
  children,
  style,
  textStyle,
  lightColor,
  darkColor,
  ...props
}: AvatarProps) => {
  const theme = themeSelector();
  const [imageError, setImageError] = useState(false);

  // Get initials from children or alt text
  const getInitials = () => {
    const text = children || alt;
    if (!text) return '?';

    const words = text.trim().split(' ');
    if (words.length >= 2) {
      return `${words[0][0]}${words[1][0]}`.toUpperCase();
    }
    return text.substring(0, 2).toUpperCase();
  };

  // Generate a color based on the text
  const getBackgroundColor = () => {
    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8',
      '#F7DC6F', '#BB8FCE', '#85C1E2', '#F8B739', '#52B788'
    ];
    const text = children || alt || '';
    const index = text.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
    return colors[index];
  };

  const containerStyle = {
    width: size,
    height: size,
    borderRadius: size / 2,
    overflow: 'hidden' as const,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    backgroundColor: theme ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)',
  };

  const fallbackStyle = {
    ...containerStyle,
    backgroundColor: getBackgroundColor(),
  };

  const textStyles = {
    color: 'white',
    fontSize: size * 0.4,
    fontWeight: 'bold' as const,
  };

  // Handle image source
  const imageSource = typeof src === 'string'
    ? { uri: src }
    : src;

  return (
    <View style={[containerStyle, style]} {...props}>
      {imageSource && !imageError ? (
        <Image
          source={imageSource}
          style={{ width: size, height: size }}
          onError={() => setImageError(true)}
          resizeMode="cover"
        />
      ) : (
        <View style={fallbackStyle}>
          <DefaultText style={[textStyles, textStyle]}>
            {getInitials()}
          </DefaultText>
        </View>
      )}
    </View>
  );
};

export const AvatarGroup = ({
  avatars = [],
  max = 5,
  size = 40,
  spacing = -10,
  style,
  renderSurplus,
  lightColor,
  darkColor,
  ...props
}: AvatarGroupProps) => {
  const theme = themeSelector();

  // Calculate how many avatars to show
  const displayAvatars = avatars.slice(0, max);
  const surplus = Math.max(0, avatars.length - max);

  return (
    <View
      style={[
        {
          flexDirection: 'row',
          alignItems: 'center',
          height: size,
        },
        style
      ]}
      {...props}
    >
      {displayAvatars.map((avatar, index) => (
        <View
          key={index}
          style={{
            marginLeft: index === 0 ? 0 : spacing,
            zIndex: displayAvatars.length - index,
            borderWidth: 2,
            borderColor: theme ? 'white' : '#333333',
            borderRadius: size / 2,
            backgroundColor: theme ? 'white' : '#333333',
          }}
        >
          <Avatar
            src={avatar.src}
            alt={avatar.alt}
            size={size}
          >
            {avatar.name || avatar.alt}
          </Avatar>
        </View>
      ))}

      {surplus > 0 && (
        <View
          style={{
            marginLeft: spacing,
            zIndex: 0,
            borderWidth: 2,
            borderColor: theme ? 'white' : '#333333',
            borderRadius: size / 2,
            backgroundColor: theme ? 'white' : '#333333',
          }}
        >
          {renderSurplus ? (
            renderSurplus(surplus)
          ) : (
            <View
              style={{
                width: size,
                height: size,
                borderRadius: size / 2,
                backgroundColor: theme ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.2)',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <DefaultText
                style={{
                  color: theme ? 'white' : '#333333',
                  fontSize: size * 0.35,
                  fontWeight: 'bold',
                }}
              >
                +{surplus}
              </DefaultText>
            </View>
          )}
        </View>
      )}
    </View>
  );
};